import React, { useMemo } from "react";
import { DEFAULT_COL_WIDTH, DEFAULT_ROW_HEIGHT, HEADER_COL_WIDTH, HEADER_ROW_HEIGHT } from "../../constants";
import { computePane, PANE_Z } from "./paneClip";

/**
 * ExcelImage renders ONE pane-copy of an embedded image, mirroring
 * ExcelChart's freeze-pane behaviour: the part of an image inside the
 * frozen rows/columns stays pinned while the rest scrolls.
 */
function ExcelImage({
  image, colWidths, rowHeights, zoomScale, hiddenCols, hiddenRows,
  pane = "body",
  freezeX = HEADER_COL_WIDTH, freezeY = HEADER_ROW_HEIGHT,
  scrollX = 0, scrollY = 0,
}) {
  // Compute position and size synchronously to avoid a flash at (0,0)
  const { position, imageSize } = useMemo(() => {
    if (!image.range) return { position: { top: 0, left: 0 }, imageSize: { width: image.width, height: image.height } };

    const range = image.range;
    const headerRowHeight = HEADER_ROW_HEIGHT;
    const headerColWidth = HEADER_COL_WIDTH; // 44px - matches CSS

    // Hidden rows/cols are not rendered by the grid — skip them here too.
    const isColHidden = (i) => !!(hiddenCols && hiddenCols.has && hiddenCols.has(i));
    const isRowHidden = (i) => !!(hiddenRows && hiddenRows.has && hiddenRows.has(i));

    // Calculate top-left position
    let tlLeft = 0, tlTop = 0;
    for (let i = 0; i < (range.tl?.col || 0); i++) {
      if (!isColHidden(i)) tlLeft += (colWidths[i] || DEFAULT_COL_WIDTH);
    }
    for (let i = 0; i < (range.tl?.row || 0); i++) {
      if (!isRowHidden(i)) tlTop += (rowHeights[i] || DEFAULT_ROW_HEIGHT);
    }

    // Add header offsets
    tlLeft += headerColWidth;
    tlTop += headerRowHeight;

    // Add EMU sub-cell offsets
    if (range.tl?.colOff) tlLeft += range.tl.colOff / 914400 * 96;
    if (range.tl?.rowOff) tlTop += range.tl.rowOff / 914400 * 96;

    let width = image.width;
    let height = image.height;

    // Calculate dimensions from bottom-right anchor
    if (range.br) {
      let brLeft = 0, brTop = 0;
      for (let i = 0; i < (range.br.col || 0); i++) {
        if (!isColHidden(i)) brLeft += (colWidths[i] || DEFAULT_COL_WIDTH);
      }
      for (let i = 0; i < (range.br.row || 0); i++) {
        if (!isRowHidden(i)) brTop += (rowHeights[i] || DEFAULT_ROW_HEIGHT);
      }
      brLeft += headerColWidth;
      brTop += headerRowHeight;

      if (range.br.colOff) brLeft += range.br.colOff / 914400 * 96;
      if (range.br.rowOff) brTop += range.br.rowOff / 914400 * 96;

      width = Math.max(20, brLeft - tlLeft);
      height = Math.max(20, brTop - tlTop);
    }

    return { position: { top: tlTop, left: tlLeft }, imageSize: { width, height } };
  }, [image, colWidths, rowHeights, hiddenCols, hiddenRows]);

  // Freeze-pane geometry for THIS pane (content px); null => nothing here.
  const paneGeom = useMemo(
    () => computePane(pane, position.left, position.top, imageSize.width, imageSize.height, freezeX, freezeY, scrollX, scrollY),
    [pane, position.left, position.top, imageSize.width, imageSize.height, freezeX, freezeY, scrollX, scrollY]
  );

  if (!paneGeom) return null;

  // Scale position (incl. pane pin offset) and size by zoomScale.
  const scaledTop = (position.top + (paneGeom.offsetY || 0)) * zoomScale;
  const scaledLeft = (position.left + (paneGeom.offsetX || 0)) * zoomScale;
  const scaledWidth = imageSize.width * zoomScale;
  const scaledHeight = imageSize.height * zoomScale;
  const zIndex = PANE_Z[pane] || 10;
  const clipStyle = paneGeom.clipPath
    ? { clipPath: paneGeom.clipPath, WebkitClipPath: paneGeom.clipPath }
    : null;

  return (
    <div
      className="excel-image-overlay"
      data-pane={pane}
      data-cl={position.left}
      data-ct={position.top}
      data-cw={imageSize.width}
      data-ch={imageSize.height}
      style={{
        position: 'absolute',
        top: scaledTop,
        left: scaledLeft,
        width: scaledWidth,
        height: scaledHeight,
        pointerEvents: 'auto',
        zIndex,
        ...clipStyle,
      }}
    >
      <img
        src={image.dataUrl}
        alt=""
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'contain',
          maxWidth: 'none',
          maxHeight: 'none',
        }}
        draggable={false}
      />
    </div>
  );
}

// PERF: memo'd — image objects and size arrays are stable per sheet.
export default React.memo(ExcelImage);
